import { useTranslation } from 'react-i18next';
import { Cross1Icon } from '@radix-ui/react-icons';
import { BookingField, TicketType } from '@packages/types';
import { Input, Textarea, Tooltip, SectionRule } from '@/components';
import { TicketingOwnerContext, TicketItem, fieldValue, useTicketOrder } from './useTicketOrder';
import { formatEur } from './format';

const ROW_ACCENTS = [
  'var(--ww-orange)',
  'var(--ww-periwinkle)',
  'var(--ww-olive)',
  'var(--ww-chartreuse)',
];

/** Compact ticket-type row: qty stepper + description + optional "?" tooltip. */
const TicketTypeRow = ({
  type,
  accent,
  count,
  onChange,
}: {
  type: TicketType;
  accent: string;
  count: number;
  onChange: (next: number) => void;
}) => {
  const { t, i18n } = useTranslation();
  const qbtn =
    'flex h-[38px] w-[38px] cursor-pointer select-none items-center justify-center font-mono text-lg font-bold text-ww-charcoal disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <div
      className="flex items-center justify-between gap-4 p-[15px] pl-[18px]"
      style={{
        background: count
          ? 'color-mix(in srgb, var(--ww-chartreuse) 22%, var(--paper-50))'
          : 'var(--paper-50)',
        border: '2px solid var(--ww-charcoal)',
        boxShadow: count ? '4px 4px 0 var(--ww-charcoal)' : 'none',
      }}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <span
            className="shrink-0"
            style={{
              width: 12,
              height: 12,
              background: accent,
              border: '2px solid var(--ww-charcoal)',
            }}
          />
          <span className="font-sans text-[17px] font-bold tracking-[0.02em] text-ww-charcoal">
            {type.label}
          </span>
          {type.description ? (
            <Tooltip content={type.description}>
              <button
                type="button"
                aria-label={t('more_info')}
                className="inline-flex h-[18px] w-[18px] cursor-help items-center justify-center rounded-full border-[1.5px] border-ink-soft font-mono text-[11px] font-bold text-ink-soft">
                ?
              </button>
            </Tooltip>
          ) : null}
          <span className="ml-0.5 font-mono text-[18px] font-bold text-ww-orange">
            {formatEur(type.price, i18n.language)}
          </span>
        </div>
        {type.description ? (
          <p className="mt-1.5 max-w-[460px] font-sans text-sm leading-snug text-ink-soft">
            {type.description}
          </p>
        ) : null}
      </div>
      <div
        className="flex shrink-0 items-center"
        style={{ border: '2px solid var(--ww-charcoal)' }}>
        <button type="button" className={qbtn} onClick={() => onChange(Math.max(0, count - 1))}>
          −
        </button>
        <span
          className="flex h-[38px] w-[34px] items-center justify-center font-mono text-lg font-bold"
          style={{
            borderLeft: '2px solid var(--ww-charcoal)',
            borderRight: '2px solid var(--ww-charcoal)',
            background: 'var(--paper-200)',
          }}>
          {count}
        </span>
        <button type="button" className={qbtn} onClick={() => onChange(count + 1)}>
          +
        </button>
      </div>
    </div>
  );
};

const CustomField = ({
  field,
  value,
  onChange,
  missing,
}: {
  field: BookingField;
  value: string;
  onChange: (v: string) => void;
  missing?: boolean;
}) => {
  const { t } = useTranslation();
  const Comp = field.type === 'textarea' ? Textarea : Input;
  return (
    <label className="ww-field mt-3.5 block">
      <span className="ww-label">
        {field.label}
        {field.required ? '' : ` (${t('optional')})`}
      </span>
      <Comp
        className={missing ? 'ww-input--missing' : undefined}
        required={field.required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {field.helpText ? (
        <p className="mt-1 font-sans text-xs text-ink-soft">{field.helpText}</p>
      ) : null}
    </label>
  );
};

/** Attendee card with per-ticket fields + missing-data highlighting. */
const CartTicket = ({
  index,
  item,
  label,
  price,
  incomplete,
  attendeeFields,
  onUpdate,
  onDelete,
}: {
  index: number;
  item: TicketItem;
  label: string;
  price: number;
  incomplete: boolean;
  attendeeFields: BookingField[];
  onUpdate: (t: TicketItem) => void;
  onDelete: () => void;
}) => {
  const { t, i18n } = useTranslation();
  return (
    <article
      className="mb-5"
      style={{
        background: 'var(--ww-chartreuse)',
        border: '2px solid var(--ww-charcoal)',
        boxShadow: '5px 5px 0 var(--ww-charcoal)',
        padding: '24px 26px',
      }}>
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="ww-eyebrow" style={{ color: 'var(--ww-charcoal)' }}>
            {t('ticket')} {index + 1}
          </div>
          <h3 className="mt-1 font-sans text-xl font-bold tracking-[0.02em] text-ww-charcoal">
            {label}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {incomplete ? (
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.06em] bg-ww-orange px-2 py-1 text-paper-50">
              ⚠ {t('data_missing')}
            </span>
          ) : null}
          <span className="font-mono text-2xl font-bold text-ww-charcoal">
            {formatEur(price, i18n.language)}
          </span>
          <button
            type="button"
            onClick={onDelete}
            aria-label={t('cancel')}
            className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-ww-charcoal text-paper-50">
            <Cross1Icon />
          </button>
        </div>
      </header>
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <label className="ww-field m-0 block">
          <span className="ww-label">{t('email')}</span>
          <Input
            type="email"
            required
            placeholder="name@mail.de"
            className={incomplete && !item.email.trim() ? 'ww-input--missing' : undefined}
            value={item.email}
            onChange={(e) => onUpdate({ ...item, email: e.target.value })}
          />
        </label>
        <label className="ww-field m-0 block">
          <span className="ww-label">
            {t('full_name')}
            {incomplete && !item.name.trim() ? (
              <em className="not-italic text-ww-orange"> · {t('required_field')}</em>
            ) : null}
          </span>
          <Input
            required
            placeholder="Vorname Nachname"
            className={incomplete && !item.name.trim() ? 'ww-input--missing' : undefined}
            value={item.name}
            onChange={(e) => onUpdate({ ...item, name: e.target.value })}
          />
        </label>
      </div>
      {attendeeFields.map((field) => (
        <CustomField
          key={field.key}
          field={field}
          value={fieldValue(item.customFields, field)}
          missing={incomplete && field.required && !fieldValue(item.customFields, field).trim()}
          onChange={(v) =>
            onUpdate({ ...item, customFields: { ...item.customFields, [field.key]: v } })
          }
        />
      ))}
    </article>
  );
};

export const FestivalTicketOrder = ({ context }: { context: TicketingOwnerContext }) => {
  const { t, i18n } = useTranslation();
  const order = useTicketOrder(context);
  const eur = (n: number) => formatEur(n, i18n.language);

  if (order.isLoading) return null;

  return (
    <form id="ticketForm" className="grid grid-cols-1 gap-12 lg:grid-cols-[2fr_1fr]">
      {/* LEFT */}
      <div>
        <SectionRule>{t('choose_tickets')}</SectionRule>
        <div className="mb-11 flex flex-col gap-3">
          {order.ticketTypes.map((type, i) => (
            <TicketTypeRow
              key={type.ticketTypeId ?? type.name}
              type={type}
              accent={ROW_ACCENTS[i % ROW_ACCENTS.length]}
              count={order.countForType(type)}
              onChange={(next) => order.setTicketCount(type, next)}
            />
          ))}
          {!order.ticketTypes.length ? (
            <p className="font-sans text-ink-soft">{t('noTicketsAvailable')}</p>
          ) : null}
        </div>

        <div ref={order.ticketsRef}>
          {order.tickets.length ? (
            <SectionRule accent="var(--ww-orange)">
              {t('your_tickets')} ({order.tickets.length})
            </SectionRule>
          ) : null}
          {order.ticketsWithType.map((line, index) => (
            <CartTicket
              key={line.ticket.rowId}
              index={index}
              item={line.ticket}
              label={line.ticketType?.label ?? `${t('ticket')} ${index + 1}`}
              price={line.ticketType?.price ?? 0}
              incomplete={order.attempted && order.ticketIncomplete(line.ticket)}
              attendeeFields={order.attendeeFields}
              onUpdate={order.updateTicket}
              onDelete={() => order.deleteTicket(line.ticket)}
            />
          ))}
        </div>

        <div className="mt-2">
          <SectionRule>{t('buyer')}</SectionRule>
          <label className="ww-field block">
            <span className="ww-label">{t('buyer_email')}</span>
            <Input
              type="email"
              required
              placeholder="name@mail.de"
              value={order.buyerEmail}
              onChange={(e) => order.setBuyerEmail(e.target.value)}
            />
          </label>
          {order.orderFields.map((field) => (
            <CustomField
              key={field.key}
              field={field}
              value={fieldValue(order.buyerFields, field)}
              missing={
                order.attempted && field.required && !fieldValue(order.buyerFields, field).trim()
              }
              onChange={(v) => order.setBuyerFields((prev) => ({ ...prev, [field.key]: v }))}
            />
          ))}
        </div>
      </div>

      {/* RIGHT — sticky Zusammenfassung */}
      <aside>
        <div
          className="ww-grain ww-grain--soft sticky top-6"
          style={{
            background: 'var(--ww-charcoal)',
            color: 'var(--paper-50)',
            border: '3px solid var(--ww-charcoal)',
            boxShadow: 'var(--shadow-lift)',
            padding: '26px 26px 30px',
            transform: 'rotate(-1deg)',
          }}>
          <div className="ww-eyebrow" style={{ color: 'var(--ww-chartreuse)' }}>
            {t('summary')}
          </div>
          <table className="mt-4 w-full border-collapse font-mono text-sm">
            <tbody>
              {order.summary.lineItems.length ? (
                <>
                  {order.summary.lineItems.map((li) => (
                    <tr
                      key={li.ticketTypeId}
                      style={{ borderBottom: '1px dashed rgba(250,250,248,.25)' }}>
                      <td className="py-2 text-paper-200">
                        {li.count} × {li.label}
                      </td>
                      <td className="py-2 text-right">{eur(li.lineTotal)}</td>
                    </tr>
                  ))}
                  {order.summary.serviceFee > 0 ? (
                    <tr style={{ borderBottom: '1px dashed rgba(250,250,248,.25)' }}>
                      <td className="py-2 text-paper-200">
                        {t('service_fee', { percent: order.summary.serviceFeePercent })}
                      </td>
                      <td className="py-2 text-right">{eur(order.summary.serviceFee)}</td>
                    </tr>
                  ) : null}
                </>
              ) : (
                <tr>
                  <td className="py-2 text-paper-300">{t('no_tickets_selected')}</td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="mt-5">
            <div className="ww-eyebrow" style={{ color: 'var(--ww-chartreuse)' }}>
              {t('grand_total')}
            </div>
            <div className="font-mono text-[46px] font-bold leading-none tracking-[-0.02em] text-paper-50">
              {eur(order.summary.grandTotal)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => order.onSubmit('ticketForm')}
            disabled={!order.tickets.length || order.isPending}
            className="ww-btn ww-btn--primary ww-btn--block ww-btn--lg mt-5">
            {t('order_tickets')} →
          </button>
          {order.error ? (
            <p className="mt-2 font-sans text-sm text-ww-orange">{order.error}</p>
          ) : null}
          <p className="mt-3.5 font-sans text-xs leading-relaxed text-paper-300">
            {t('max_tickets_note')}
          </p>
        </div>
      </aside>
    </form>
  );
};
